import {observable, autorun, computed, action} from 'mobx';
import autobind from 'autobind-decorator';
import {lazyInitialize, nonenumerable} from 'core-decorators';
import moment from 'moment-timezone';

import APIRequestState from 'spa/state/_util/api-request';

import uuidv4 from 'uuid/v4';

class NewWorkersState extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/search/workers/range'; }
}

class NewWorkerSlotsState extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/search/slots'; }
}

class WorkersState extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/search/availableWorkersForDate'; }
}

class SpecialitiesState extends APIRequestState {
  get method() { return 'GET'; }
  get url() { return '/specialities'; }
}

class WorkerDatesState extends APIRequestState {
  get method() { return 'GET'; }
  get url() { return '/dates'; }
}

class WorkerSlotsState extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/search/availableSlotsForDate'; }
}

class TherapistsState {
  /*
   * Return the selection context via our treatments state
   */
  get selection() { return this.ctx.selection; }

  /*
   * Return the current address context
   */
  @computed get address() {
    return this.ctx.address;
  }

  /*
   * Return the current selected gender or default to male
   */
  @computed get male() {
    return this.selection.gender === '' ? undefined : this.selection.gender === 'male';
  }

  /*
   * Lazy load our required models / states
   */
  @lazyInitialize @nonenumerable specialities = new SpecialitiesState(this.ctx);
  @lazyInitialize @nonenumerable workerDates = new WorkerDatesState(this.ctx);
  @lazyInitialize @nonenumerable workerSlots = new WorkerSlotsState(this.ctx);

  @lazyInitialize @nonenumerable workers = this.newSearchWorkers ? new NewWorkersState(this.ctx) : new WorkersState(this.ctx);

  @nonenumerable
  listeners = [];

  /*
   * Return the full details of the currently 'active' therapist, active meaning the user is viewing
   * their bio / availability but hasn't actually 'selected' them yet
   */
  @computed get activeViewTherapist() {
    const id = this.selection.therapistInfoViewing;
    if (!id) return undefined;
    const instance = this.ctx.workers.getOneInstance(id);
    if (!instance) setTimeout(this.ctx.workers.maybeLoadOne, 0, id);
    return instance;
  }

  /*
   * Return the full details of the currently 'active' treatment, active meaning the user is viewing
   * its details but hasn't actually 'selected' them yet
   */
  @computed get activeViewTreatment() {
    const id = this.selection.treatmentInfoViewing;
    if (!id) return undefined;
    const instance = this.ctx.specialities.getOneInstance(id);
    if (!instance) setTimeout(this.ctx.specialities.maybeLoadOne, 0, id);
    return instance;
  }

  /*
   * Create a search request object to load the available workers
   */
  @computed get workersSearchRequest() {
    if (!this.startOfDay) return null;

    if (!this.newSearchSlots) {
      const request = {
        time: this.startOfDay,
        serviceAreaContext: this.address.response.serviceArea.id,
        minimumWorkers: 1,
        product: 1,
        speciality_ids: this.selection.filteredTreatments,
        location: {
          lat: this.address.response.formatted.lat,
          lng: this.address.response.formatted.lng,
          serviceArea: this.address.response.serviceArea.id,
        },
        male: this.male,
        sort: this.selection.sort === '' ? '' : `-${this.selection.sort}`,
      };

      if (this.endOfRangeDay) {
        request.start = this.startOfDay;
        request.end = this.endOfRangeDay;
      }

      return request;
    }

    const {timezone} = this.address.response.city;

    const endDateRange = this.endOfRangeDay ? this.endOfRangeDay : this.endOfDay;

    let workerMatrix;

    if (this.selection.filteredTreatments && this.selection.filteredTreatments.length) {
      workerMatrix = this.selection.filteredTreatmentsMatrix.length ? {
        worker1: this.selection.filteredTreatmentsMatrix,
      } : null;
    }

    let sorting = [];

    if (this.selection.sort === 'rating') {
      sorting = [
        {
          name: 'rating',
        },
      ];
    }

    if (
      this.selection.sort &&
      typeof this.selection.sort === 'string' &&
      this.selection.sort.includes('traits')
    ) {
      sorting = [
        {
          name: 'traits',
          type: this.selection.sort.split('.')[1],
        },
      ];
    }

    return {
      matrix: workerMatrix,
      range: {
        start: !this.selectedSlot ? this.startOfDay : this.selectedSlot.start,
        end: !this.selectedSlot ? endDateRange : null,
        timezone,
      },
      isMale: this.male,
      location: {
        lat: this.address.response.formatted.lat,
        lng: this.address.response.formatted.lng,
        serviceArea: this.address.response.serviceArea.id,
      },
      sort: sorting,
    };
  }

  /*
   * Load the available workers
   */
  @autobind @action maybeLoadWorkers() {
    const {workers, workersSearchRequest} = this;

    if (workersSearchRequest) {
      const {tracker, umDeviceIdentifier} = this.ctx;

      tracker.workersSearchID = uuidv4();

      let currentSearchRequest;

      if (!this.newSearchWorkers) {
        currentSearchRequest = {
          bookingFlowID: tracker.bookingFlowID,
          umDeviceIdentifier,
          workersSearchID: tracker.workersSearchID,
          ...workersSearchRequest,
        };
      } else {
        currentSearchRequest = {
          ...workersSearchRequest,
          tracking: {
            bookingFlowID: tracker.bookingFlowID,
            umDeviceIdentifier,
            workersSearchID: tracker.workersSearchID,
          },
        };
      }

      workers.maybeLoad({}, currentSearchRequest).then((response) => {
        if (response) {
          tracker.track('Success response workers search', {
            searchData: currentSearchRequest,
            workersSearchID: tracker.workersSearchID,
          });
        }
      }).catch((error) => {
        tracker.track('Error response workers search', {
          responseError: error && error.json,
          searchData: currentSearchRequest,
          workersSearchID: tracker.workersSearchID,
        });
      });
    }
  }

  /*
   * Create a search request object to load the available workers
   */
  @computed get workersCitySearchRequest() {
    const {address} = this;
    if (!address || !address.response) return null;
    if (!address.response.city) return null;
    const startDate = moment
      .tz(moment(), this.selection.DATE_FORMAT, this.address.response.city.timezone)
      .startOf('day')
      .unix();

    const endDate = moment
      .tz(moment(), this.selection.DATE_FORMAT, this.address.response.city.timezone)
      .add(1, 'day')
      .endOf('day')
      .unix();

    if (!this.newSearchSlots) {
      return {
        time: startDate,
        start: startDate,
        end: endDate,
        minimumWorkers: 1,
        product: 1,
        location: {
          lat: this.address.response.formatted.lat,
          lng: this.address.response.formatted.lng,
          serviceArea: this.address.response.serviceArea.id,
        },
      };
    }

    const {timezone} = this.address.response.city;

    return {
      // For now the city landing pages only mention massage, once we update them we need to update this as well,
      // to make the request per each vertical
      vertical_id: 'massage',
      range: {
        start: startDate,
        end: endDate,
        timezone,
      },
      isMale: this.male,
      location: {
        lat: this.address.response.formatted.lat,
        lng: this.address.response.formatted.lng,
        serviceArea: this.address.response.serviceArea.id,
      },
    };
  }

  /*
   * Load the available workers for a city page
   */
  @autobind @action maybeLoadCityWorkers() {
    if (this.workersCitySearchRequest) {
      this.workers.maybeLoad({}, this.workersCitySearchRequest);
    }
  }

  /*
   * Create a search request object to load the available dates for a specific worker
   */
  @computed get workerDatesSearchRequest() {
    if (!this.address.response) return null;
    return {
      timezone: this.address.response &&
        this.address.response.city &&
        this.address.response.city.timezone,
      workers: this.selection.therapistInfoViewing,
      serviceArea: this.address.response.serviceArea.id,
      product: 1,
      location: {
        lat: this.address.response.formatted.lat,
        lng: this.address.response.formatted.lng,
        serviceArea: this.address.response.serviceArea.id,
      },
    };
  }

  /*
   * Load the available dates for a specific worker
   */
  @autobind @action maybeLoadWorkerDates() {
    if (this.workerDatesSearchRequest) {
      this.workerDates.maybeLoad(this.workerDatesSearchRequest);
    }
  }

  /*
   * Create a search request object to load the available slots for a specific date and worker
   */
  @computed get workerSlotsSearchRequest() {
    if (!this.startOfDay || !this.selection.therapistInfoViewing) return null;

    return {
      workers: [this.selection.therapistInfoViewing],
      time: this.startOfDay,
      serviceArea: this.address.response.serviceArea.id,
      product: this.selection.people === 1 ?
        [this.selection.duration] :
        [this.selection.duration, this.selection.duration2],
      location: {
        lat: this.address.response.formatted.lat,
        lng: this.address.response.formatted.lng,
        serviceArea: this.address.response.serviceArea.id,
      },
    };
  }

  /*
   * Load the slots for a specific worker
   */
  @autobind @action maybeLoadWorkerSlots(specialityId, duration) {
    const {tracker, umDeviceIdentifier} = this.ctx;
    const {selection, workerSlots, workerSlotsSearchRequest} = this;

    if (duration) {
      selection.duration = duration;
    }

    if (workerSlotsSearchRequest) {
      if (specialityId) {
        workerSlotsSearchRequest.speciality = specialityId;
      }

      tracker.slotSearchID = uuidv4();

      let currentSearchRequest;

      if (!this.newSearchSlots) {
        currentSearchRequest = {
          bookingFlowID: tracker.bookingFlowID,
          slotSearchID: tracker.slotSearchID,
          umDeviceIdentifier,
          ...workerSlotsSearchRequest,
        };
      } else {
        currentSearchRequest = {
          ...workerSlotsSearchRequest,
          tracking: {
            bookingFlowID: tracker.bookingFlowID,
            slotSearchID: tracker.slotSearchID,
            umDeviceIdentifier,
          },
        };
      }

      workerSlots.maybeLoad({}, currentSearchRequest).then((response) => {
        if (response) {
          tracker.track('Success response slot search', {
            searchData: currentSearchRequest,
            slotSearchID: tracker.slotSearchID,
          });
        }
      }).catch((error) => {
        tracker.track('Error response slot search', {
          responseError: error && error.json,
          searchData: currentSearchRequest,
          slotSearchID: tracker.slotSearchID,
        });
      });
    }
  }

  /*
   * Return a unix timestamp for the start of the day
   */
  @computed get startOfDay() {
    if (!this.selection.date || !this.address.response) return null;

    return moment
      .tz(this.selection.date, this.selection.DATE_FORMAT, this.address.response.city.timezone)
      .startOf('day')
      .unix();
  }

  /*
   * Return a unix timestamp for the end of the day
   */
  @computed get endOfDay() {
    if (!this.selection.date || !this.address.response) return null;

    return moment
      .tz(this.selection.date, this.selection.DATE_FORMAT, this.address.response.city.timezone)
      .add(1, 'day')
      .startOf('day')
      .unix();
  }

  /*
   * Return a unix timestamp for the start of the end of range day
   */
  @computed get endOfRangeDay() {
    if (!this.selection.endDate || !this.address.response) return null;
    return moment
      .tz(this.selection.endDate, this.selection.DATE_FORMAT, this.address.response.city.timezone)
      .add(1, 'day')
      .startOf('day')
      .unix();
  }

  @computed get workerDateOptions() {
    const {i18n} = this.ctx;
    const {response} = this.workerDates;
    const {timezone} = this.address.response.city;

    const dates = [];

    if (response && response.dates) {
      for (const date of response.dates) {
        const mmnt = moment.unix(date.timestamp).tz(timezone).locale(i18n.lc);
        const key = mmnt.format(this.selection.DATE_FORMAT);
        const selected = this.selection.date === key;

        dates.push({
          date: mmnt.format('DD'),
          day: mmnt.format('ddd'),
          key,
          disabled: !date.available,
          selected,
        });
      }
    }

    return dates;
  }

  @nonenumerable
  listeners = [];

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    const {config, i18n} = this.ctx;

    this.newSearchSlots = config.getValue('feature.b2c.new-search.enable-new-booking-slot-search-web', i18n.umRegion);

    this.newSearchWorkers = config.getValue('feature.b2c.new-search.enable-new-booking-workers-search-web', i18n.umRegion);
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default TherapistsState;



// WEBPACK FOOTER //
// ./spa/Pages/Therapists/state.jsx